/**
 * useSPBAnalyticsData — data fetching hook for the SPB Analytics plugin.
 *
 * Orchestrates: check DynamoDB cache → fetch target+baseline via
 * getASFMPreProcessedData → generate report via reportGenerator →
 * generate AI summaries via submitQuestion+poll → save to cache.
 *
 * Returns PluginDataResult { data, isLoading, error, refetch }.
 *
 * ──────────────────────────────────────────────────────────────────
 * MIGRATION CONTEXT
 * ──────────────────────────────────────────────────────────────────
 * This hook replaces the standalone processForFinn Lambda pipeline
 * (source: docs/reference/processForFinn/). The old pipeline was:
 *
 *   1. User submits request → JSON written to S3 `finn-requests` bucket
 *   2. S3 PutObject event triggers processForFinn Lambda
 *   3. Lambda runs Athena queries via query_store.py, generates HTML
 *      via html_generator.py, invokes FinnEmail Step Function for AI
 *   4. Results written back to S3; frontend polls for completion
 *
 * The new architecture eliminates steps 1-4 entirely:
 *   - Data fetching uses the existing getASFMPreProcessedData GraphQL
 *     query (same Athena backend, no new Lambda needed)
 *   - Report generation runs client-side (TypeScript port of html_generator.py)
 *   - AI summaries use the existing askAI GraphQL mutation directly
 *   - Caching uses DynamoDB via AppSync direct resolvers
 *
 * DEPRECATION PLAN — S3 event trigger on `finn-requests` bucket:
 *   Phase 1 (current): Both old S3 pipeline and new FINN-native dashboard
 *           are active. The S3 trigger remains enabled for backward compat.
 *   Phase 2: After validation that the new dashboard produces equivalent
 *           results, disable the S3 event trigger on `finn-requests`.
 *   Phase 3: Remove the processForFinn Lambda, FinnEmail Step Function,
 *           and associated S3 bucket event configuration.
 * ──────────────────────────────────────────────────────────────────
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import type { PluginDataResult, DashboardFilters } from "../../types";
import type { ReportData, AnalysisMetadata } from "../../utils/reportGenerator";
import { generateReportFromData } from "../../utils/reportGenerator";
import { buildCacheKey, buildSPBAnalysisId } from "../../utils/cacheUtils";
import { getMarketplaceList } from "../../utils/marketplaceUtils";
import type { Marketplace, Region } from "../../utils/marketplaceUtils";
import {
  getDashboardCache,
  saveDashboardCache,
  fetchPreProcessedData,
  fetchPreProcessedDataAsync,
  generateAISummary,
  fetchMarketplaces,
  fetchRegions,
} from "../../services/dashboardService";
import {
  buildMarketplaceAnalysisPrompt,
  buildTrendAnalysisPrompt,
} from "./promptBuilder";
import type { PromptMetadata } from "./promptBuilder";

const DASHBOARD_ID = "spb-analytics";
const CACHE_TTL_SECONDS = 7 * 24 * 60 * 60; // 7 days

/**
 * Service user credentials for AI summary generation.
 *
 * The revenue bridging dashboard uses a dedicated app user (not the
 * logged-in user) to generate AI summaries via submitQuestion.
 *
 * - DASHBOARD_SERVICE_USER_ID = 0 : revenue bridging dashboard app user
 * - DASHBOARD_SERVICE_TEAM_ID = 1 : FINN team
 */
const DASHBOARD_SERVICE_USER_ID = 0;
const DASHBOARD_SERVICE_TEAM_ID = 1;

/** Shape of the data returned by this hook. */
export interface SPBAnalyticsData {
  reportData: ReportData | null;
  marketplaceAnalysis: string | null;
  trendAnalysis: string | null;
  aiLoading: boolean;
  aiError: string | null;
  geoGroupName: string;
  analysisDataStr: string;
  forecastDataStr: string;
  trendDataStr: string;
  /** Subtitle info for the report header */
  product: string;
  device: string;
  targetDateRange: string;
}

/**
 * Custom hook implementing the SPB Analytics data fetching flow.
 */
export function useSPBAnalyticsData(
  rawFilters: Record<string, any>
): PluginDataResult {
  const filters = rawFilters as Partial<DashboardFilters>;

  const [data, setData] = useState<SPBAnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Geo data cached in ref to avoid re-fetching
  const geoDataRef = useRef<{
    regions: Region[];
    marketplaces: Marketplace[];
  } | null>(null);

  const fetchGeoData = useCallback(async () => {
    if (geoDataRef.current) return geoDataRef.current;
    const [mkt, reg] = await Promise.all([fetchMarketplaces(), fetchRegions()]);
    geoDataRef.current = { regions: reg, marketplaces: mkt };
    return geoDataRef.current;
  }, []);

  const fetchData = useCallback(async () => {
    // Don't fetch if no dates are set
    if (!filters.targetStartDate || !filters.targetEndDate) return;
    if (!filters.baselineStartDate || !filters.baselineEndDate) return;

    // Cancel any in-flight request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);

    try {
      const product = filters.product ?? "SP";
      const geo = filters.geo ?? "ww";
      const deviceFilter = filters.device ?? "Desktop-Mobile";
      // API expects "Desktop+Mobile" but filter stores "Desktop-Mobile"
      const device = deviceFilter.replace("-", "+");

      // Resolve geo to marketplace IDs
      const geoData = await fetchGeoData();
      const { marketplaceIds, geoGroupName } = getMarketplaceList(
        geo,
        geoData.regions,
        geoData.marketplaces
      );

      if (controller.signal.aborted) return;

      // Build cache key
      const analysisId = buildSPBAnalysisId(
        product,
        filters.targetStartDate!,
        filters.targetEndDate!,
        filters.baselineStartDate!,
        filters.baselineEndDate!
      );
      const pk = buildCacheKey(DASHBOARD_ID, analysisId);
      const sk = `${geo}#${device}`;

      // Check cache
      const cached = await getDashboardCache(pk, sk);
      if (controller.signal.aborted) return;

      if (cached?.report_data) {
        try {
          const reportData = JSON.parse(
            cached.report_data as string
          ) as ReportData;
          const aiSummaries = cached.ai_summaries
            ? JSON.parse(cached.ai_summaries as string)
            : {};

          setData({
            reportData,
            marketplaceAnalysis: aiSummaries.marketplace_analysis ?? null,
            trendAnalysis: aiSummaries.trend_analysis ?? null,
            aiLoading: false,
            aiError: null,
            geoGroupName,
            analysisDataStr: "",
            forecastDataStr: "",
            trendDataStr: "",
            product,
            device,
            targetDateRange: `${filters.targetStartDate}~${filters.targetEndDate}`,
          });
          setIsLoading(false);
          return;
        } catch (parseErr) {
          console.warn("Cache parse error, treating as miss:", parseErr);
        }
      }

      // Cache miss — fetch from Athena
      const today = new Date().toISOString().slice(0, 10);

      /**
       * QUERY MAPPING: processForFinn query_store.py → getASFMPreProcessedData
       * ─────────────────────────────────────────────────────────────────────
       * Old (query_store.py `get_query()`):
       *   - query_name:      'preprocessed_perf_query' | 'forecast_traffic_query' | 'trend_query' | 'latest_dataset_date_query'
       *   - run_date:         max(target_end_date) + 3 days
       *   - date:             (start_date, end_date) tuple
       *   - geo_list:         list of marketplace IDs from common.get_marketplace_list(geo)
       *   - geo_group_name:   region display name (e.g. 'WW', 'NA', 'US') from common.get_marketplace_list(geo)
       *   - product:          'SP' | 'SB'
       *   - date_group:       'target' | 'baseline'
       *   - device:           'Desktop+Mobile' | 'Desktop' | 'Mobile'
       *
       * New (ASFMPreProcessedDataInput via getASFMPreProcessedData):
       *   - query_name:       Same — 'preprocessed_perf_query'
       *   - run_date:         today's date (ISO string)
       *   - date_ranges:      JSON-stringified [start_date, end_date] array
       *   - geo_list:         JSON-stringified array of marketplace IDs (from RDS region_marketplace table, replacing common.py hardcoded mapping)
       *   - geo_group_name:   Same — region display name from RDS region table
       *   - product:          Same — 'SP' | 'SB'
       *   - date_group:       Same — 'target' | 'baseline'
       *   - device:           Same — 'Desktop-Mobile' | 'Desktop' | 'Mobile' (note: hyphen instead of plus)
       *   - database:         'asfm' (was 'sandbox' in processForFinn)
       *
       * Key differences:
       *   1. Geo resolution now uses RDS tables (marketplace, region, region_marketplace)
       *      fetched via getMarketplaces/getRegions GraphQL queries, replacing the
       *      hardcoded common.py get_marketplace_mapping() / get_region_mapping().
       *   2. date_ranges is a JSON string array instead of a Python tuple.
       *   3. run_date uses today instead of target_end_date + 3 days.
       *   4. database changed from 'sandbox' to 'asfm'.
       */
      const baseParams = {
        product,
        geo_list: marketplaceIds,
        geo_group_name: geoGroupName,
        device,
        database: "asfm",
        run_date: today,
      };

      const dateRangesJson = JSON.stringify({
        target: {
          start: filters.targetStartDate,
          end: filters.targetEndDate,
        },
        baseline: {
          start: filters.baselineStartDate,
          end: filters.baselineEndDate,
        },
      });

      // Fetch all four query types in parallel (matching old processForFinn):
      // 1. preprocessed_perf_query (target) — placement-level KPIs
      // 2. preprocessed_perf_query (baseline) — same for baseline period
      // 3. forecast_traffic_query — actual vs forecasted pageviews
      // 4. trend_query — YoY/MoM/QoQ/WoW revenue trends
      const comparisonMode = (filters.comparisonMode ?? "yoy").toLowerCase();

      const [targetResult, baselineResult, forecastResult, trendResult] =
        await Promise.all([
          fetchPreProcessedData({
            ...baseParams,
            query_name: "preprocessed_perf_query",
            date_group: "target",
            date_ranges: dateRangesJson,
          } as any),
          fetchPreProcessedData({
            ...baseParams,
            query_name: "preprocessed_perf_query",
            date_group: "baseline",
            date_ranges: dateRangesJson,
          } as any),
          fetchPreProcessedDataAsync({
            ...baseParams,
            query_name: "forecast_traffic_query",
            date_ranges: dateRangesJson,
          } as any)
            .then((res: any) => {
              console.log(
                "[Dashboard] forecast_traffic_query response:",
                JSON.stringify({
                  statusCode: res?.statusCode,
                  rowCount: res?.rowCount ?? res?.rows?.length ?? 0,
                  hasData: !!res,
                })
              );
              return res;
            })
            .catch((err: any) => {
              console.error("[Dashboard] forecast_traffic_query failed:", err);
              return null;
            }),
          fetchPreProcessedDataAsync({
            ...baseParams,
            query_name: "trend_query",
            analysis_type: comparisonMode,
            date_ranges: dateRangesJson,
          } as any)
            .then((res: any) => {
              console.log(
                "[Dashboard] trend_query response:",
                JSON.stringify({
                  statusCode: res?.statusCode,
                  rowCount: res?.rowCount ?? res?.rows?.length ?? 0,
                  hasData: !!res,
                  sampleRow: res?.rows?.[0] ?? null,
                })
              );
              return res;
            })
            .catch((err: any) => {
              console.error("[Dashboard] trend_query failed:", err);
              return null;
            }),
        ]);

      if (controller.signal.aborted) return;

      // Parse data — API returns {columns[], rows[], rowCount, ...}
      // Convert columns + rows into DataRow[] objects
      const parseAthenaResult = (result: any): any[] => {
        if (!result) return [];
        // If already an array of objects (e.g. from cache), use directly
        if (Array.isArray(result)) return result;
        // API returns {rows: [...], columns: [...], ...}
        // rows can be array of objects (keyed by column name) or array of arrays
        const rows: any[] = result.rows || [];
        if (rows.length === 0) return [];
        // If first row is already an object (not array), return as-is
        if (!Array.isArray(rows[0])) return rows;
        // If rows are arrays, map columns to create objects
        const columns: string[] = (result.columns || []).map(
          (c: any) => c.name || c
        );
        return rows.map((row: any[]) => {
          const obj: Record<string, string> = {};
          columns.forEach((col: string, i: number) => {
            obj[col] = row[i] != null ? String(row[i]) : "";
          });
          return obj;
        });
      };

      const targetData = parseAthenaResult(targetResult);
      const baselineData = parseAthenaResult(baselineResult);
      const forecastData = forecastResult
        ? parseAthenaResult(forecastResult)
        : [];
      const trendData = trendResult ? parseAthenaResult(trendResult) : [];

      console.log("[Dashboard] Parsed data counts:", {
        target: targetData.length,
        baseline: baselineData.length,
        forecast: forecastData.length,
        trend: trendData.length,
        trendResultRaw: trendResult
          ? { rowCount: trendResult.rowCount, rows: trendResult.rows?.length }
          : "null",
        forecastResultRaw: forecastResult
          ? {
              rowCount: forecastResult.rowCount,
              rows: forecastResult.rows?.length,
            }
          : "null",
      });

      // Generate report
      const metadata: AnalysisMetadata = {
        event_content: {
          page_type:
            product === "SP" ? "Search+Detail+Homepage" : "Search+Detail",
          device,
          target_date_range: [filters.targetStartDate!, filters.targetEndDate!],
          baseline_date_range: [
            filters.baselineStartDate!,
            filters.baselineEndDate!,
          ],
        },
      };

      const reportData = generateReportFromData(
        targetData,
        baselineData,
        metadata,
        geoGroupName,
        product
      );

      // Set report data immediately (AI summaries load async)
      setData({
        reportData,
        marketplaceAnalysis: null,
        trendAnalysis: null,
        aiLoading: true,
        aiError: null,
        geoGroupName,
        analysisDataStr: JSON.stringify(targetData),
        forecastDataStr: JSON.stringify(forecastData),
        trendDataStr: JSON.stringify(trendData),
        product,
        device,
        targetDateRange: `${filters.targetStartDate}~${filters.targetEndDate}`,
      });
      setIsLoading(false);

      // Generate AI summaries in background (inline to avoid
      // parameter-passing issues with separate useCallback)
      (async () => {
        const promptMeta: PromptMetadata = {
          product,
          targetDateStart: filters.targetStartDate!,
          targetDateEnd: filters.targetEndDate!,
          baselineDateStart: filters.baselineStartDate!,
          baselineDateEnd: filters.baselineEndDate!,
          comparisonMode: filters.comparisonMode ?? "YoY",
        };

        const analysisDataStr = JSON.stringify({
          target: targetData,
          baseline: baselineData,
        });
        const forecastDataStr = JSON.stringify(forecastData);
        const trendDataStr = JSON.stringify(trendData);

        let mktAnalysis: string | null = null;
        let trdAnalysis: string | null = null;
        let aiError: string | null = null;

        try {
          const mktPrompt = buildMarketplaceAnalysisPrompt(
            analysisDataStr,
            promptMeta,
            forecastDataStr,
            geoGroupName,
            device
          );
          const trendPrompt = buildTrendAnalysisPrompt(
            trendDataStr,
            promptMeta,
            geoGroupName,
            device
          );

          const [mR, tR] = await Promise.allSettled([
            generateAISummary(
              uuidv4(),
              DASHBOARD_SERVICE_USER_ID,
              DASHBOARD_SERVICE_TEAM_ID,
              mktPrompt
            ),
            generateAISummary(
              uuidv4(),
              DASHBOARD_SERVICE_USER_ID,
              DASHBOARD_SERVICE_TEAM_ID,
              trendPrompt
            ),
          ]);

          if (controller.signal.aborted) return;

          mktAnalysis = mR.status === "fulfilled" ? mR.value : null;
          trdAnalysis = tR.status === "fulfilled" ? tR.value : null;

          if (mR.status === "rejected" && tR.status === "rejected") {
            aiError = "AI summary generation failed";
          }
        } catch (err: any) {
          if (controller.signal.aborted) return;
          aiError = err?.message ?? "AI summary generation failed";
        }

        if (controller.signal.aborted) return;

        setData((prev) =>
          prev
            ? {
                ...prev,
                marketplaceAnalysis: mktAnalysis,
                trendAnalysis: trdAnalysis,
                aiLoading: false,
                aiError,
              }
            : prev
        );

        // Save to cache (non-blocking)
        try {
          const ttl = Math.floor(Date.now() / 1000) + CACHE_TTL_SECONDS;
          await saveDashboardCache({
            pk,
            sk,
            dashboard_id: DASHBOARD_ID,
            report_data: JSON.stringify(reportData),
            ai_summaries: JSON.stringify({
              marketplace_analysis: mktAnalysis,
              trend_analysis: trdAnalysis,
            }),
            ttl,
          } as any);
        } catch (cacheErr) {
          // eslint-disable-next-line no-console
          console.warn("Cache save failed:", cacheErr);
        }
      })();
    } catch (err: any) {
      if (!controller.signal.aborted) {
        setError(err?.message ?? "Failed to fetch dashboard data");
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, fetchGeoData]);

  // Trigger fetch when filters change (only when they have dates)
  useEffect(() => {
    if (filters.targetStartDate && filters.baselineStartDate) {
      fetchData();
    }
    return () => {
      abortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.product,
    filters.geo,
    filters.device,
    filters.targetStartDate,
    filters.targetEndDate,
    filters.baselineStartDate,
    filters.baselineEndDate,
  ]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData,
  };
}

export default useSPBAnalyticsData;

/**
 * useComparisonData — lightweight hook for the second (comparison) dataset.
 *
 * Fetches target + baseline via getASFMPreProcessedData and generates a
 * ReportData object. No AI summaries, no caching — just the report tables.
 * Triggered manually via the returned `fetch` function (not on filter change).
 */
export function useComparisonData() {
  const [data, setData] = useState<{
    reportData: ReportData;
    geoGroupName?: string;
    device?: string;
    product?: string;
    targetDateRange?: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const geoDataRef = useRef<{
    regions: import("../../utils/marketplaceUtils").Region[];
    marketplaces: import("../../utils/marketplaceUtils").Marketplace[];
  } | null>(null);

  const fetchGeoData = useCallback(async () => {
    if (geoDataRef.current) return geoDataRef.current;
    const [mkt, reg] = await Promise.all([fetchMarketplaces(), fetchRegions()]);
    geoDataRef.current = { regions: reg, marketplaces: mkt };
    return geoDataRef.current;
  }, []);

  const fetchComparison = useCallback(
    async (
      cmpFilters: Record<string, any>,
      primaryScale?: number,
      primaryBridgeScale?: number
    ) => {
      const f = cmpFilters as Partial<DashboardFilters>;
      if (!f.targetStartDate || !f.targetEndDate) return;
      if (!f.baselineStartDate || !f.baselineEndDate) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsLoading(true);
      setError(null);

      try {
        const product = f.product ?? "SP";
        const geo = f.geo ?? "ww";
        const device = (f.device ?? "Desktop-Mobile").replace("-", "+");

        const geoData = await fetchGeoData();
        const { marketplaceIds, geoGroupName } = getMarketplaceList(
          geo,
          geoData.regions,
          geoData.marketplaces
        );
        if (controller.signal.aborted) return;

        const today = new Date().toISOString().slice(0, 10);
        const baseParams = {
          product,
          geo_list: marketplaceIds,
          geo_group_name: geoGroupName,
          device,
          database: "asfm",
          run_date: today,
        };
        const dateRangesJson = JSON.stringify({
          target: { start: f.targetStartDate, end: f.targetEndDate },
          baseline: { start: f.baselineStartDate, end: f.baselineEndDate },
        });

        const [targetResult, baselineResult] = await Promise.all([
          fetchPreProcessedData({
            ...baseParams,
            query_name: "preprocessed_perf_query",
            date_group: "target",
            date_ranges: dateRangesJson,
          } as any),
          fetchPreProcessedData({
            ...baseParams,
            query_name: "preprocessed_perf_query",
            date_group: "baseline",
            date_ranges: dateRangesJson,
          } as any),
        ]);
        if (controller.signal.aborted) return;

        const parseAthenaResult = (result: any): any[] => {
          if (!result) return [];
          if (Array.isArray(result)) return result;
          const rows: any[] = result.rows || [];
          if (rows.length === 0) return [];
          if (!Array.isArray(rows[0])) return rows;
          const columns: string[] = (result.columns || []).map(
            (c: any) => c.name || c
          );
          return rows.map((row: any[]) => {
            const obj: Record<string, string> = {};
            columns.forEach((col: string, i: number) => {
              obj[col] = row[i] != null ? String(row[i]) : "";
            });
            return obj;
          });
        };

        const targetData = parseAthenaResult(targetResult);
        const baselineData = parseAthenaResult(baselineResult);

        const metadata: AnalysisMetadata = {
          event_content: {
            page_type:
              product === "SP" ? "Search+Detail+Homepage" : "Search+Detail",
            device,
            target_date_range: [f.targetStartDate!, f.targetEndDate!],
            baseline_date_range: [f.baselineStartDate!, f.baselineEndDate!],
          },
        };

        const reportData = generateReportFromData(
          targetData,
          baselineData,
          metadata,
          geoGroupName,
          product,
          primaryScale,
          primaryBridgeScale
        );

        if (!controller.signal.aborted) {
          setData({
            reportData,
            geoGroupName,
            device,
            product,
            targetDateRange: `${f.targetStartDate}~${f.targetEndDate}`,
          });
          setIsLoading(false);
        }
      } catch (err: any) {
        if (!controller.signal.aborted) {
          setError(err?.message ?? "Failed to fetch comparison data");
          setIsLoading(false);
        }
      }
    },
    [fetchGeoData]
  );

  const clear = useCallback(() => {
    abortRef.current?.abort();
    setData(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return { data, isLoading, error, fetchComparison, clear };
}
